import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getInvoices,
  getInvoice,
  previewInvoice,
  generateInvoice,
  updateInvoice,
  deleteInvoice,
  getInvoiceHistory,
  markInvoicePaid,
  unmarkInvoicePaid,
  downloadInvoiceTimesheetXlsx,
  type GenerateInvoiceInput,
  type UpdateInvoiceInput,
  type InvoicePaymentStatus,
  type InvoiceTimesheetExportOptions,
} from '../services/api/invoices';

export function useInvoices(filters?: {
  projectId?: number;
  periodId?: number;
  status?: InvoicePaymentStatus;
  /** MKS-352 — the deletion log instead of the live list. */
  deleted?: boolean;
}) {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => getInvoices(filters),
  });
}

/**
 * MKS-352 — the invoice's own audit trail.
 *
 * Same query key prefix as `useAuditTrail`'s so both are invalidated by one
 * `['auditTrail']` sweep.
 */
export function useInvoiceHistory(id: number | undefined) {
  return useQuery({
    queryKey: ['auditTrail', 'invoiceHistory', id],
    queryFn: () => getInvoiceHistory(id!),
    enabled: !!id,
  });
}

export function useInvoice(id: number | undefined) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoice(id!),
    enabled: !!id,
  });
}

export function usePreviewInvoice() {
  return useMutation({
    mutationFn: (input: GenerateInvoiceInput) => previewInvoice(input),
  });
}

export function useGenerateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateInvoiceInput) => generateInvoice(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // Phase 62 (Plan 62-06) — also invalidate the To Be Invoiced builder + queue
      // caches so the card refreshes after a selective generate.
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced'] });
      queryClient.invalidateQueries({ queryKey: ['toBeInvoicedQueue'] });
    },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateInvoiceInput & { id: number }) => updateInvoice(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * MKS-352 — `reason` is what finance typed into the delete dialog. Required:
 * the server 400s without it.
 *
 * Invalidates the detail and the audit trail as well as the list: after a soft
 * delete the invoice is still readable, and both its banner and its History
 * have changed.
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => deleteInvoice(id, reason),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail'] });
      // The released entries, receipts and billing periods are back in the
      // queue, so the builder and queue caches are stale too.
      queryClient.invalidateQueries({ queryKey: ['projectToBeInvoiced'] });
      queryClient.invalidateQueries({ queryKey: ['toBeInvoicedQueue'] });
    },
  });
}

// MKS-214 — both payment mutations invalidate the detail, the list AND the
// audit trail: the backend writes a `state_changes` row for each, and the
// detail page renders that history right below the buttons.
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paidAt }: { id: number; paidAt?: string }) => markInvoicePaid(id, paidAt),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'Invoice', id] });
    },
  });
}

export function useUnmarkInvoicePaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => unmarkInvoicePaid(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['auditTrail', 'Invoice', id] });
    },
  });
}

/**
 * MKS-350 — download the client timesheet for an invoice.
 *
 * A mutation rather than a query: it is an action with a side effect (a file
 * lands in the browser's downloads), not cacheable state. Mirrors
 * `useDownloadDetailedReportXlsx` in `useReports`.
 */
export function useDownloadInvoiceTimesheetXlsx() {
  return useMutation({
    mutationFn: ({ id, options }: { id: number; options: InvoiceTimesheetExportOptions }) =>
      downloadInvoiceTimesheetXlsx(id, options),
    onSuccess: ({ blob, filename }, { id }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `invoice-${id}-timesheet.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
